import React, { useCallback, useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import s from './MonetizationPromoBanner.scss';

export interface Props {
  className?: string;
  monetizationPromo?: string;
  profileOwnerUser: PapiUser;
}

export const MonetizationPromoBanner: React.FC<Props> = ({
  monetizationPromo,
  profileOwnerUser,
  className,
}) => {
  const { t } = useTranslation();
  const { username, userId } = profileOwnerUser;
  const isMobile = useContext(MobileContext);
  const pcp_promos = useFlag('pcp_promos');

  const checkOut = useCallback(() => {
    let checkOutURL;
    if (monetizationPromo === 'tier') {
      checkOutURL = Url.userSubscriptionsPath({
        username,
      });
    } else if (monetizationPromo === 'adoptable') {
      checkOutURL = Url.userShopLink(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.ADOPTABLES
      );
    } else if (monetizationPromo === 'premium_gallery') {
      checkOutURL = Url.userShopLink(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS
      );
    } else if (pcp_promos && monetizationPromo === 'pcp') {
      checkOutURL = Url.userShopLink(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_CONTENT
      );
    } else {
      checkOutURL = Url.userShopLink(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
      );
    }
    window.location.href = checkOutURL;
  }, [username, monetizationPromo, pcp_promos]);

  if (
    !monetizationPromo ||
    !['tier', 'commission', 'premium_gallery', 'adoptable', 'pcp'].includes(
      monetizationPromo
    )
  ) {
    return null;
  }

  if (!pcp_promos && monetizationPromo === 'pcp') {
    return null;
  }

  let biEvid: BiEventType | null = null;
  if (monetizationPromo === 'tier') {
    biEvid = BiEventType.VIEW_SUBSCRIPTION_TAB;
  } else if (monetizationPromo === 'adoptable') {
    biEvid = BiEventType.VIEW_ADOPTABLES_TAB;
  } else if (monetizationPromo === 'commission') {
    biEvid = BiEventType.VIEW_COMMISSIONS_TAB;
  } else if (monetizationPromo === 'premium_gallery') {
    biEvid = BiEventType.VIEW_PREMIUM_GALLERIES_TAB;
  }

  const biData = biEvid
    ? {
        evid: biEvid,
        typeid: ResourceType.USER,
        itemid: userId,
        referral_info: ReferralType.PROFILE_COVER_PROMOTION,
      }
    : undefined;

  return (
    <div
      className={c(
        s['root'],
        s[`type-${monetizationPromo}`],
        s['ms-ads-mobile'],
        className
      )}
    >
      <div className={s['container']}>
        <ConditionalWrapper
          condition={isMobile}
          wrap={child => (
            <BiLink onClick={checkOut} biData={biData}>
              {child}
            </BiLink>
          )}
        >
          <div className={s['body']}>
            {t(
              `common.profileHero.moneyBanner.var.${monetizationPromo}.message${
                isMobile ? '.mobile' : ''
              }`
            )}
          </div>
          {!isMobile && (
            <BiLink className={s['link']} onClick={checkOut} biData={biData}>
              {t('common.profileHero.moneyBanner.CTA')}
            </BiLink>
          )}
        </ConditionalWrapper>
      </div>
    </div>
  );
};
MonetizationPromoBanner.displayName = 'MonetizationPromoBanner';

export default MonetizationPromoBanner;
